import React from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/navbar/index.jsx';
import Footer from '../../components/Footer/Footer.js';
import WaFloat from '../../components/WaFloat/WaFloat.jsx';
import { useReveal } from '../../hooks/useReveal.js';

const services = [
  {
    n: 'I', h: 'Strategic Advisory',
    p: 'The foundation of every engagement. We assess your business, understand your objectives, and structure a clear path — whether that involves capital, expansion, or organisational change.',
    pts: ['Business strategy and capital readiness', 'Growth plan structuring and scenario modelling', 'Capital structure recommendation', 'Financial narrative and investor documentation', 'Valuation guidance and sector benchmarking'],
    link: null,
  },
  {
    n: 'II', h: 'Capital Introductions',
    p: 'We connect prepared businesses with aligned capital partners. Every introduction is structured, confidential, and preceded by thorough preparation on both sides.',
    pts: ['HNI, family office and institutional investor matching', 'Investor profiling and curated outreach', 'Private investor meeting facilitation', 'Business information pack preparation', 'Engagement process and follow-up management'],
    link: null,
  },
  {
    n: 'III', h: 'AFO',
    p: 'The AQT Financial Officer (AFO) — an embedded, senior advisory professional who works inside your business to build financial discipline and investor-readiness from within.',
    pts: ['Financial health assessment and cleanup advisory', 'MIS systems and reporting infrastructure', 'Monthly investor and board reporting', 'Post-capital governance and compliance', 'Fractional CFO and financial leadership'],
    link: { to: '/afo', label: 'AFO Engagement Details →' },
  },
  {
    n: 'IV', h: 'Expansion Strategy',
    p: 'For businesses scaling into new geographies, segments, or business models. We structure the opportunity, identify the right partners, and support through execution.',
    pts: ['Market entry and geography expansion planning', 'Joint venture and strategic partnership structuring', 'Acquisition target identification and approach', 'Cross-border capital and India entry advisory', 'Transaction documentation advisory'],
    link: null,
  },
];

export default function Businesses() {
  useReveal();
  return (
    <>
      <Navbar />

      <div className="biz-hero">
        <div className="biz-hero-left">
          <span className="sec-tag reveal">Businesses</span>
          <h2 className="sec-h reveal" style={{ maxWidth: 560 }}>
            Four disciplines in service of one objective —<br /><em>your business, built to last.</em>
          </h2>
          <p className="sec-sub reveal reveal-delay-1">
            We do not offer standardised products. Every engagement is structured around what your business actually needs.
          </p>
        </div>
        <div className="biz-hero-right reveal reveal-delay-1">
          <div className="biz-hero-afo-tag">Spotlight</div>
          <div className="biz-hero-afo-title">AQT Financial Officer</div>
          <p className="biz-hero-afo-sub">
            Most businesses want capital. Few are structured for it. The AFO works inside your company — cleaning up financials, building the investor narrative, and connecting you with aligned capital.
          </p>
          <ul className="biz-hero-afo-pts">
            <li>Embedded senior advisor · not a consultant</li>
            <li>Investor-ready in 60–90 days</li>
            <li>Works alongside your CA and finance team</li>
            <li>Stays engaged post-funding</li>
          </ul>
          <Link className="btn-primary biz-hero-afo-cta" to="/afo">Explore AFO →</Link>
        </div>
      </div>

      <hr className="rule" />

      <section className="section section-off">
        <div className="svc-grid" style={{ marginTop: 0 }}>
          {services.map(({ n, h, p, pts, link }, i) => (
            <div className={`svc reveal${i > 0 ? ` reveal-delay-${Math.min(i, 4)}` : ''}`} key={n}>
              <span className="svc-n">{n}</span>
              <h3>{h}</h3>
              <p>{p}</p>
              <ul className="svc-points">
                {pts.map(pt => <li key={pt}>{pt}</li>)}
              </ul>
              {link && <Link className="svc-link" to={link.to}>{link.label}</Link>}
            </div>
          ))}
        </div>
      </section>

      <hr className="rule" />

      {/* CAPITAL SOLUTIONS */}
      <section className="section section-off">
        <span className="sec-tag reveal">Our Capital Solutions</span>
        <h2 className="sec-h reveal">The right capital,<br /><em>structured for your stage.</em></h2>
        <p className="sec-sub reveal reveal-delay-1" style={{ maxWidth: 580, marginTop: 12 }}>
          We work across four capital structures depending on your business profile, growth objective, and risk appetite.
        </p>
        <div className="cap-sol-grid reveal reveal-delay-2">
          {[
            {
              sym: '₹', title: 'Debt Funding', sub: 'Growth Without Dilution',
              desc: 'For businesses with strong cash flows seeking growth capital without giving up equity.',
              pts: ['Structured Business Loans', 'Institutional & NBFC Debt', 'Working Capital Financing', 'Venture & Structured Debt'],
            },
            {
              sym: '↑', title: 'Pre-IPO Funding', sub: 'Preparing for the Public Market',
              desc: 'Capital that strengthens valuation and governance ahead of a public market listing.',
              pts: ['Valuation Benchmarking', 'Financial Governance Refinement', 'Pre-IPO Equity Structuring', 'Strategic Investor Onboarding'],
            },
            {
              sym: '◈', title: 'Project Funding', sub: 'Converting Vision into Capital',
              desc: 'Structured feasibility and capital mix for large-scale, infrastructure-linked projects.',
              pts: ['DPR Evaluation & Restructuring', 'Debt-Equity Optimisation', 'Institutional Capital Alignment', 'Risk Mitigation Frameworks'],
            },
            {
              sym: '◉', title: 'Private Equity', sub: 'Strategic Capital for Scale',
              desc: 'Long-term partnership capital for growth-stage companies ready to scale significantly.',
              pts: ['Expansion Capital Structuring', 'Minority & Strategic Equity', 'Cap-Table & Dilution Planning', 'Term-Sheet Strategy & Negotiation'],
            },
          ].map(({ sym, title, sub, desc, pts }) => (
            <div className="cap-sol-card" key={title}>
              <span className="cap-sol-sym">{sym}</span>
              <h3>{title}</h3>
              <div className="cap-sol-sub">{sub}</div>
              <p>{desc}</p>
              <ul className="cap-sol-pts">
                {pts.map(pt => <li key={pt}>{pt}</li>)}
              </ul>
            </div>
          ))}
        </div>
      </section>

      <hr className="rule" />

      {/* AFO SPOTLIGHT — Section 1: The gap */}
      <section className="section section-off">
        <div className="grid-2">
          <div>
            <span className="sec-tag reveal">AQT Financial Officer</span>
            <h2 className="sec-h reveal">Most businesses want capital.<br /><em>Few are ready for it.</em></h2>
            <p className="sec-sub reveal reveal-delay-1">
              The AFO is an embedded, senior advisor who works inside your business — not as a consultant who visits once a quarter, but as a hands-on financial professional building the discipline investors actually look for.
            </p>
            <p className="sec-sub reveal reveal-delay-1" style={{ marginTop: 12 }}>
              Most growing companies have no full-time CFO, mixed personal and business finances, and no investor-ready narrative. The AFO fixes all three — from within.
            </p>
          </div>
          <div className="who-list reveal reveal-delay-1">
            {[
              { n: '01', h: 'No full-time CFO', p: 'Promoter manages finances. CA handles compliance only. No one is focused on making the company attractive to capital.' },
              { n: '02', h: 'Weak financial presentation', p: 'Mixed expenses, unclear EBITDA, poor working capital visibility — all instant red flags in investor due diligence.' },
              { n: '03', h: 'No investor narrative', p: 'Companies cannot articulate their growth story, TAM, or competitive moat in a language investors respond to.' },
              { n: '04', h: 'Wrong capital structure', p: 'Taking debt when equity fits better, or diluting too early — costly mistakes an experienced AFO prevents.' },
            ].map(({ n, h, p }) => (
              <div className="who-item" key={n}>
                <span className="who-num">{n}</span>
                <div className="who-content"><h4>{h}</h4><p>{p}</p></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <hr className="rule" />

      {/* AFO SPOTLIGHT — Section 2: What the AFO delivers */}
      <section className="section biz-afo-section">
        <div className="biz-afo-header reveal">
          <div>
            <span className="sec-tag">What the AFO Delivers</span>
            <h2 className="sec-h">From audit to capital —<br /><em>every step covered.</em></h2>
            <p className="sec-sub">A structured engagement that takes a business from growth ambition to investor-ready — and then connects it with aligned capital partners.</p>
            <div style={{ marginTop: 28 }}>
              <Link className="btn-primary" to="/afo">Full AFO Details →</Link>
            </div>
          </div>
          <div className="biz-afo-badge">
            <div className="biz-afo-badge-inner">
              <div className="biz-afo-badge-num">6</div>
              <div className="biz-afo-badge-label">Stage<br />Engagement</div>
            </div>
            <div className="biz-afo-badge-inner">
              <div className="biz-afo-badge-num">60</div>
              <div className="biz-afo-badge-label">Day<br />Readiness</div>
            </div>
            <div className="biz-afo-badge-inner">
              <div className="biz-afo-badge-num">1</div>
              <div className="biz-afo-badge-label">Senior<br />Advisor</div>
            </div>
          </div>
        </div>
        <div className="biz-afo-grid reveal reveal-delay-1">
          {[
            { n: '01', h: 'Financial Health Audit', out: 'Full diagnostic with priority action list' },
            { n: '02', h: 'Books Cleanup & Restructuring', out: 'Investor-ready financials in 60–90 days' },
            { n: '03', h: 'Capital Structure Advisory', out: 'Capital plan with dilution scenario modelling' },
            { n: '04', h: 'Investor Narrative & IM', out: 'Full IM, pitch deck, and financial model' },
            { n: '05', h: 'Investor Connect', out: 'Structured introductions to aligned capital' },
            { n: '06', h: 'Post-Funding Governance', out: 'MIS, board reporting, investor relations' },
          ].map(({ n, h, out }) => (
            <div className="biz-afo-card" key={n}>
              <span className="biz-afo-card-n">{n}</span>
              <h4>{h}</h4>
              <span className="outcome">{out}</span>
            </div>
          ))}
        </div>
      </section>

      <div className="cta-strip reveal">
        <h2>Every engagement begins<br />with <em>a private conversation.</em></h2>
        <p>Tell us about your business and what you are trying to achieve. We will be direct about whether and how we can help.</p>
        <div className="cta-btns">
          <Link className="btn-white" to="/contactus">Register Interest</Link>
        </div>
      </div>

      <Footer />
      <WaFloat />
    </>
  );
}
